import { useEffect, useRef } from "react";
import { useIsMutating, useMutation, useQueryClient, type MutationKey, type QueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

import { byLens, metricLabel, refreshStrategies, STRATEGIES_COPY as T, strategyRefusal, type Metric } from "./strategies";

/**
 * What every part of the Strategies editor (C1 v1.15) shares: the one write idiom (and whether a keyed write is pending), the
 * tab's result line, the no-data marker and the metric select grouped by lens. Nothing here works a figure out: C2 answers, the
 * editor says what it answered.
 */

/** The tab's one result line (design call R): C2's answer in words, or the refusal. */
export type Result = { tone: "ok" | "err"; text: string } | null;
/** What each part needs from the tab: the area for the refusal sentence, the result line, and the in-gate dialog anchor (null
 * until it is on screen, so no dialog ever mounts at the body and escapes the permission gate). */
export type Editor = { areaName: string; report: (result: Result) => void; anchor: HTMLElement | null };

/** A strategy write's options: its sentence on success, C2's refusal in words on error, and after either the one refresh
 * (`refreshStrategies`), because most refusals mean the page is behind the store. `settled` runs first, so a dialog that reads
 * the strategy being deleted can close before the refresh would ask for it again. The refresh is awaited: TanStack keeps the
 * write pending until the page has C2's new rule ids and lanes, so a control disabled while it is pending cannot send the old. */
export function writeOptions<V, A>(editor: Editor, client: QueryClient, write: (vars: V) => Promise<A>, done: (answer: A, vars: V) => string, settled?: () => void) {
  return {
    mutationFn: write,
    onSuccess: (answer: A, vars: V) => editor.report({ tone: "ok", text: done(answer, vars) }),
    onError: (error: unknown) => editor.report({ tone: "err", text: strategyRefusal(error, editor.areaName) }),
    onSettled: async () => { settled?.(); await refreshStrategies(client); },
  };
}
/** `key` names a write whose pending state another part reads (`useWritePending`): a card's Rename or Edit goal (`editStrategyKey`). */
export function useStrategyWrite<V, A>(editor: Editor, write: (vars: V) => Promise<A>, done: (answer: A, vars: V) => string, settled?: () => void, key?: MutationKey) {
  return useMutation({ ...writeOptions(editor, useQueryClient(), write, done, settled), mutationKey: key });
}
/** Whether a keyed write is pending, read from the client's mutation cache rather than from the part that sent it: it holds from
 * the click until the write's refresh has landed, even when the form that sent it has closed (Cancel while it saves). */
export function useWritePending(key: MutationKey): boolean {
  return useIsMutating({ mutationKey: key }) > 0;
}

/** AGENTS.md rule 14: a refusal is an alert (inverted portal-wide); a success stays a status line (as the New price box). The one
 * line sits at the page head (design call R) while the control refused may be far down the map, so a refusal scrolls it into view. */
export function ResultLine({ result }: { result: Result }) {
  const line = useRef<HTMLParagraphElement>(null);
  useEffect(() => revealRefusal(result, line.current), [result]);
  if (!result) return null;
  return <p ref={line} role={result.tone === "err" ? "alert" : "status"} className={result.tone === "err" ? "text-sm" : "text-sm text-ks-pass"}>{result.text}</p>;
}
/** Brings a refusal's line into view (every report is a new object, so a repeated refusal scrolls again); a success stays put. */
export function revealRefusal(result: Result, line: Pick<HTMLElement, "scrollIntoView"> | null): void {
  if (result?.tone === "err") line?.scrollIntoView({ block: "nearest", behavior: "smooth" });
}
/** A form's own check (v26's inline error), inverted like every alert. */
export function FormError({ error }: { error: string | null }) {
  return error ? <p role="alert" className="text-sm">{error}</p> : null;
}

/** Why the evaluator never judges this metric (design call K), in C2's words. */
export function NoData({ reason }: { reason: string | null | undefined }) {
  return reason ? <Badge variant="outline" className="ml-2 text-ks-grey-1">{T.library.noData(reason)}</Badge> : null;
}

export function SectionHead({ title, desc, children }: { title: string; desc?: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><h3 className="text-base font-semibold text-ks-ink">{title}</h3>{desc && <p className="text-sm text-ks-grey-1">{desc}</p>}</div>
      {children}
    </div>
  );
}

/** Every metric of the store's vocabulary, grouped by lens as the drawer heads them (design call J; flat before C2 v1.17). */
export function MetricSelect({ metrics, value, onChange, label, placeholder }: { metrics: Metric[]; value: string; onChange: (metric: string) => void; label: string; placeholder: string }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-56" aria-label={label}><SelectValue placeholder={placeholder}>{value ? metricLabel(metrics, value) : undefined}</SelectValue></SelectTrigger>
      <SelectContent>
        {byLens(metrics).map((g) => (
          <SelectGroup key={g.lens ?? "none"}>
            {g.label && <SelectLabel>{g.label}</SelectLabel>}
            {g.items.map((m) => <SelectItem key={m.metric} value={m.metric}>{m.label}</SelectItem>)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
/** "%" after a threshold or target in points; nothing after money (price per serving). */
export const unitMark = (metrics: Metric[], metric: string) => (metrics.find((m) => m.metric === metric)?.unit === "money" ? "" : "%");

/** The strategy's colour as a dot before its name (C1 v1.35); nothing when the strategy has no colour. Decorative: the name says it. */
export function StrategyDot({ colour }: { colour: string | null }) {
  if (!colour) return null;
  return <span aria-hidden data-strategy-dot={colour} className="inline-block size-2.5 shrink-0 rounded-full" style={{ backgroundColor: colour }} />;
}
